export type ReportSessionSnapshot = {
  total_time_seconds: number;
  max_scroll_depth: number;
  sections_viewed_count: number;
  total_clicks: number;
  is_bounce: boolean;
};

const BOUNCE_TIME_THRESHOLD_SECONDS = 15;

export const createReportSessionMetrics = () => {
  let accumulatedMs = 0;
  let visibleSince: number | null = null;
  let maxScrollDepth = 0;
  let totalClicks = 0;
  const sectionsViewed = new Set<string>();
  let started = false;

  const pauseActiveTime = () => {
    if (visibleSince == null) return;
    accumulatedMs += Date.now() - visibleSince;
    visibleSince = null;
  };

  const resumeActiveTime = () => {
    if (document.hidden || visibleSince != null) return;
    visibleSince = Date.now();
  };

  const getTotalTimeSeconds = () => {
    const activeMs =
      accumulatedMs + (visibleSince != null ? Date.now() - visibleSince : 0);
    return Math.floor(activeMs / 1000);
  };

  const updateScrollDepth = () => {
    const doc = document.documentElement;
    const scrollHeight = Math.max(
      doc.scrollHeight,
      document.body?.scrollHeight ?? 0
    );
    if (scrollHeight <= 0) return;

    const scrolled = window.scrollY + window.innerHeight;
    const depth = Math.min(100, Math.round((scrolled / scrollHeight) * 100));
    if (depth > maxScrollDepth) {
      maxScrollDepth = depth;
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      pauseActiveTime();
      return;
    }
    resumeActiveTime();
  };

  const handleScroll = () => {
    updateScrollDepth();
  };

  const handleClick = () => {
    totalClicks += 1;
  };

  const markSectionViewed = (sectionId: string) => {
    if (!sectionId) return;
    sectionsViewed.add(sectionId);
  };

  const getSnapshot = (): ReportSessionSnapshot => {
    const totalTimeSeconds = getTotalTimeSeconds();
    return {
      total_time_seconds: totalTimeSeconds,
      max_scroll_depth: maxScrollDepth,
      sections_viewed_count: sectionsViewed.size,
      total_clicks: totalClicks,
      is_bounce:
        totalClicks === 0 && totalTimeSeconds < BOUNCE_TIME_THRESHOLD_SECONDS,
    };
  };

  const start = () => {
    if (started || typeof window === 'undefined') return;
    started = true;
    resumeActiveTime();
    updateScrollDepth();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick, true);
  };

  const stop = () => {
    if (!started) return;
    pauseActiveTime();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClick, true);
    started = false;
  };

  return {
    start,
    stop,
    markSectionViewed,
    getSnapshot,
  };
};

export type ReportSessionMetrics = ReturnType<typeof createReportSessionMetrics>;
